import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import API_URLS from "@constants/api.urls";
import { DEFAULT_WATER_TYPE, LANGUAGE_CODES, TECHNOLOGIES, USER_INFO } from "@constants/global.constants";

import { convertListToObj, groupBy, isDevEnv, isProdEnv } from "@utils/appUtils";
import DupontLogger from "@utils/DupontLogger";

import { useToastNotification } from "@components/WPToastNotification";

import { updateGlobalData, updateGlobalROElementsData } from "@common/GlobalDataSlice";
import { getLicensedProducts } from "@common/header/UserSettings/utils/UserSettings.utils";
import { updateAppVersion, updateIXPermission } from "@common/UserInfoSlice";
import { updateGUnitConversion } from "@common/utils/GlobalUnitConversionSlice";

import { getRequest } from "@api/baseApi";

// DEV only feature , DEV team members who can access multi-delete functionality or quick project create for testing purposes
const multiDeleteUsers = [
  "pardeep.sharma@dupont.com",
  "taushik.khan@dupont.com",
  "mohamad.akramm@dupont.com",
  "moroju.s.teja@dupont.com",
  "agawale.g.singh@dupont.com",
  "priyanka.sinha-1@dupont.com",
  "ramesh.k.prajapathi@dupont.com",
];

const ROUnfilteredUserList = ["wavepro.test@yopmail.com", "taushik.khan@dupont.com", "pardeep.sharma@dupont.com"];

const Logger = DupontLogger("useGlobalDataHandler");

const useGlobalDataHandler = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { showErrorNotification } = useToastNotification();

  const globalAppData = useSelector(state => state.globalAppData);
  const { data: unitConversionData } = useSelector(state => state.GUnitConversion);
  const { isIXPermission, isROPermission, data: userData } = useSelector(state => state.userInfo);

  const isMultiDeleteEnabled = useMemo(() => multiDeleteUsers.includes(userData?.EmailId), [userData?.EmailId]);

  const fetchCountries = async () => {
    if (globalAppData.countries.length > 0) return;
    try {
      const response = await getRequest(API_URLS.countriesList);
      dispatch(updateGlobalData({ type: "countries", value: response.data }));
    } catch (error) {
      Logger.error("Error fetching countries: ", error);
    }
  };

  const fetchLanguages = async () => {
    if (globalAppData.languages.length > 0) return globalAppData.languages;
    try {
      const { data: value } = await getRequest(API_URLS.languageList);
      dispatch(updateGlobalData({ type: "languages", value }));
      return value;
    } catch (error) {
      Logger.error("Error fetching languages: ", error);
    }
  };
  const fetchTranslatedLanguages = async (languageId = 1) => {
    const langCode = LANGUAGE_CODES[languageId]?.code ?? USER_INFO.cultureName;
    const existingTranslations = globalAppData.translatedLanguages || {};
    if (existingTranslations[langCode]) return;

    try {
      const { data } = await getRequest(API_URLS.languageTranslation, {}, { headers: { "x-language-id": languageId } });
      dispatch(
        updateGlobalData({
          type: "translatedLanguages",
          value: {
            ...existingTranslations,
            [langCode]: data.translations,
          },
        }),
      );
    } catch (error) {
      Logger.error("Error fetching translated languages: ", error);
    }
  };

  const translatedLanguage = useMemo(() => {
    if (globalAppData.translatedLanguages && globalAppData.translatedLanguages[language]) {
      return globalAppData.translatedLanguages[language];
    }
    return null;
  }, [globalAppData.translatedLanguages, t]);

  const fetchWaterLibraryList = async () => {
    if (globalAppData.waterLibraryList.predefinedLibrary) return;

    try {
      const { data } = await getRequest(API_URLS.waterLibrary);
      const waterLibraryList = data.reduce(
        (acc, item) => {
          item.isSystem ? acc.predefinedLibrary.push(item) : acc.userLibrary.push(item);
          return acc;
        },
        { predefinedLibrary: [], userLibrary: [] },
      );

      dispatch(updateGlobalData({ type: "waterLibraryList", value: waterLibraryList }));
    } catch (error) {
      Logger.error("Error fetching waterLibraryData: ", error);
    }
  };

  const transformChemicalsData = chemicals =>
    chemicals.reduce((acc, chemical) => {
      const { categoryId } = chemical;
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(chemical);
      return acc;
    }, {});

  const fetchChemicalCategory = async () => {
    if (globalAppData.chemicalCategoryData.chemicalCategories) return;
    try {
      const { data } = await getRequest(API_URLS.chemicalLibraryCategory, {}, { headers: { "x-language-id": 1 } });
      const { chemicals, chemicalCategories, chemicalProperties } = data;
      const chemicalsByCategoryId = transformChemicalsData(chemicals);
      const chemicalPropertiesById = chemicalProperties.reduce(
        (acc, item) => ({ ...acc, [item.chemicalId]: item }),
        {},
      );
      dispatch(
        updateGlobalData({
          type: "chemicalCategoryData",
          value: { chemicalCategories, chemicalsByCategoryId, chemicalPropertiesById },
        }),
      );
    } catch (error) {
      Logger.error("Error fetching Chemical Library: ", error);
    }
  };

  const fetchChemicalLibrary = async (isForceRefersh, languageId = 1) => {
    const langCode = LANGUAGE_CODES[languageId]?.code ?? USER_INFO.cultureName;
    const existingChemicals = globalAppData.chemicalLibraryList || {};
    if (!isForceRefersh && existingChemicals[langCode]) return;
    try {
      const { data } = await getRequest(API_URLS.chemicalLibrary, {}, { headers: { "x-language-id": languageId } });
      const sortedData = data
        .sort((item1, item2) => item2.isSystem - item1.isSystem)
        .map((item, index) => ({ ...item, localId: index }));
      dispatch(
        updateGlobalData({
          type: "chemicalLibraryList",
          value: {
            ...existingChemicals,
            [langCode]: sortedData,
          },
        }),
      );
    } catch (error) {
      Logger.error("Error fetching Chemical Library: ", error);
    }
  };

  const fetchUnreadNotifications = useCallback(async showMessage => {
    try {
      const response = await getRequest(API_URLS.notifications);
      dispatch(updateGlobalData({ type: "unreadNotifications", value: response.data }));
    } catch (error) {
      if (showMessage) {
        showErrorNotification(t("fetchNotificationFailed"));
      }
      Logger.error("Error fetching unread notifications: ", error);
    }
  }, []);

  const fetchMarketSegments = useCallback(async () => {
    if (globalAppData.marketSegments.length > 0) return;
    try {
      const { data } = await getRequest(API_URLS.marketSegment);
      dispatch(updateGlobalData({ type: "marketSegments", value: data }));
    } catch (error) {
      Logger.error("Error fetching unread notifications: ", error);
    }
  }, []);

  const fetchUnitConversionData = useCallback(async () => {
    if (unitConversionData.length > 0) return;
    try {
      const { data } = await getRequest(API_URLS.unitConversionData);
      dispatch(updateGUnitConversion(data));
    } catch (error) {
      Logger.error("Error fetching unit conversion data: ", error);
    }
  }, [unitConversionData]);

  const fetchApplicationVersion = useCallback(async () => {
    if (userData?.applicationVersion) return;
    try {
      const { data } = await getRequest(API_URLS.applicationVersion);
      dispatch(updateAppVersion(data.responseMessage));
    } catch (error) {
      Logger.error("Error fetching application version: ", error);
    }
  }, []);

  const fetchDefaultTechnology = useCallback(async () => {
    try {
      if (globalAppData.defaultTechnologies.length > 0) return;
      let { data: technologies } = await getRequest(API_URLS.defaultTechnology);
      if (!isROPermission) {
        technologies = technologies.filter(({ technologyID }) => technologyID !== 2);
      }
      const isNoneTechDefault = technologies.every(tech => !tech.isDefault);
      // If all are false, set all the technology object's `isDefault` to true(As per bug 135834)
      if (isNoneTechDefault && technologies.length) {
        technologies = technologies.map(tech => ({ ...tech, isDefault: true }));
      }
      dispatch(updateGlobalData({ type: "defaultTechnologies", value: technologies }));
    } catch (error) {
      Logger.error("Error fetching application version: ", error);
    }
  }, []);

  const checkIXPermission = useCallback(async userEmail => {
    const isProd = isProdEnv();
    try {
      let hasIXAccess = true;
      if (isProd) {
        const { data } = await getRequest(API_URLS.ixdUsers);
        hasIXAccess = data.some(({ email }) => email === userEmail);
      }
      dispatch(updateIXPermission(hasIXAccess));
      return hasIXAccess;
    } catch (error) {
      Logger.error("Error fetching fetchIXDUser: ", error);
      if (isProd) {
        dispatch(updateIXPermission(false));
        return false;
      }
    }
  }, []);

  const isTechDisabled = useCallback(
    techName =>
      (techName?.toUpperCase() === TECHNOLOGIES.IX && !isIXPermission) ||
      (techName?.toUpperCase() === TECHNOLOGIES.RO && !isROPermission),
    [isIXPermission],
  );

  const fetchExampleProjects = useCallback(async () => {
    try {
      if (globalAppData?.exampleProjects?.length > 0) return globalAppData.exampleProjects;
      const { data } = await getRequest(API_URLS.exampleProject);
      if (Array.isArray(data)) {
        dispatch(updateGlobalData({ type: "exampleProjects", value: data }));
        return data;
      }
      return [];
    } catch (error) {
      Logger.error("Error in fetching example project", error);
      return [];
    }
  }, [globalAppData?.exampleProjects?.length, isIXPermission, dispatch]);

  const fetchDefaultFeedSetupData = useCallback(async () => {
    try {
      if (globalAppData.defaultFeedSetup && globalAppData.compoundDefaults?.length) return;
      const { data } = await getRequest(API_URLS.defaultFeedSetup);
      if (data?.streams[0]) {
        const feedStream = {
          ...data.streams[0],
          ...DEFAULT_WATER_TYPE,
          streamName: t("streamNumber"),
        };
        dispatch(updateGlobalData({ type: "compoundDefaults", value: data.compoundDefaults || [] }));
        dispatch(updateGlobalData({ type: "defaultFeedSetup", value: feedStream }));
        return data;
      }
    } catch (error) {
      Logger.error("Error in fetching example project", error);
    }
  });

  const fetchROElements = async (isForceRefresh = false) => {
    if (!isROPermission) {
      return;
    }
    if (!isForceRefresh && globalAppData.elementsData?.items?.length > 0) return globalAppData.elementsData;
    try {
      const params = { PageSize: 100 };
      const { data: elementsData } = await getRequest(API_URLS.elementsData, params);

      const licensedElements = getLicensedProducts(elementsData?.items, userData?.userPermissions);
      const roElementsDataByGroup = groupBy(licensedElements, "type");
      const roElementsDataByIds = convertListToObj(elementsData?.items, "elementId");

      dispatch(updateGlobalROElementsData({ elementsData, roElementsDataByGroup, roElementsDataByIds }));
      return elementsData;
    } catch (error) {
      Logger.error("Error fetching elements data: ", error);
    }
  };

  const unfilteredElements = useMemo(() => {
    if (!isDevEnv()) return null;

    const email = userData?.EmailId?.toLowerCase();
    if (!email || !ROUnfilteredUserList.includes(email)) return null;

    return globalAppData.elementsData?.items?.filter(elem => !elem.isExperimental) ?? null;
  }, [userData?.EmailId, globalAppData.elementsData?.items]);

  const fetchGlobalData = () => {
    fetchCountries();
    fetchMarketSegments();
    fetchDefaultTechnology();
    fetchUnitConversionData();
    fetchApplicationVersion();
    fetchUnreadNotifications();
    fetchDefaultFeedSetupData();
    fetchWaterLibraryList();
    fetchExampleProjects();
    fetchROElements();
  };

  return {
    unfilteredElements,
    translatedLanguage,
    isMultiDeleteEnabled,
    isTechDisabled,
    fetchLanguages,
    fetchGlobalData,
    checkIXPermission,
    fetchChemicalLibrary,
    fetchChemicalCategory,
    fetchDefaultTechnology,
    fetchROElements,
    fetchTranslatedLanguages,
    fetchUnreadNotifications,
  };
};

export default useGlobalDataHandler;
